import { useMemo } from 'react';

import {
  activeOptionsStaleTime,
  missingSelected,
  TBroadcastActiveOptions,
  TOptionItem,
} from './active-options';
import { ActiveOptionsListbox } from './ActiveOptionsListbox';
import { TUseCatalogueOptions } from './CatalogueActiveOptions';

/** Extra narrowing a catalogue applies to the active ids, never to the selection it names. */
export type TCatalogueNarrowing = { labelsIn?: string[] };

/** Either the given active ids, optionally narrowed further, or the given ids as they are. */
export type TBroadcastCatalogueRequest =
  | { active: string[]; narrowing?: TCatalogueNarrowing }
  | { idIn: string[] };

type BroadcastCatalogueActiveOptionsProps<T> = {
  activeOptions: TBroadcastActiveOptions;
  useOptions: TUseCatalogueOptions<T, TBroadcastCatalogueRequest>;
  toItem: (entity: T) => TOptionItem;
  /** Which of the broadcast's ids this catalogue lists. */
  dimension: 'people' | 'logos';
  narrowing?: TCatalogueNarrowing;
  isDark?: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
  onClose?: () => void;
};

/**
 * The options of a filter narrowed to the ids a broadcast source carries, named by id, plus the
 * selected values they leave out. Mounted only while the dropdown is open.
 */
export function BroadcastCatalogueActiveOptions<T>({
  activeOptions,
  useOptions,
  toItem,
  dimension,
  narrowing,
  isDark,
  selected,
  onChange,
  onClose,
}: BroadcastCatalogueActiveOptionsProps<T>) {
  const ids = activeOptions.ids?.[dimension];

  const activeQuery = useOptions(
    { active: ids ?? [], narrowing },
    {
      enabled: Boolean(ids?.length),
      staleTime: activeOptionsStaleTime(activeOptions.source),
    }
  );
  const active = useMemo(
    () => (activeQuery.data?.data ?? []).map(toItem),
    [activeQuery.data, toItem]
  );

  const isFetching = activeOptions.isFetching || activeQuery.isFetching;
  const isError = activeOptions.isError || activeQuery.isError;

  const missing = missingSelected(active, selected);
  const { data: resolved } = useOptions(
    { idIn: missing },
    { enabled: ids !== undefined && !isFetching && missing.length > 0 }
  );
  const resolvedSelected = useMemo(() => (resolved?.data ?? []).map(toItem), [resolved, toItem]);

  return (
    <ActiveOptionsListbox
      isDark={isDark}
      active={active}
      resolvedSelected={resolvedSelected}
      isFetching={isFetching}
      isError={isError}
      selected={selected}
      onChange={onChange}
      onClose={onClose}
    />
  );
}
